import { Building, Check, LayoutGrid, Plus, Shield, User } from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { observer } from "mobx-react-lite";
import {
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { useOrganizationContext } from "@/lib/context/OrganizationContext";
import { useAuth } from "@/hooks/useAuth";
import { usePermissions } from "@/hooks/usePermissions";
import { useOrganizationStore } from "@/hooks/useOrganizationStore";
import { useAuthStore } from "@/hooks/useAuthStore";
import { OrgLogo } from "@/components/ui/org-logo";
import { cn } from "@/lib/utils";
import { SidebarMenuItem } from "../molecules/SidebarMenuItem";
import { SidebarNavItem } from "../molecules/SidebarNavItem";
import { hasLegacyBillingAccess, useBillingStatus } from "@/domains/billing/BillingStatusContext";
import { PAGE_INFO_GUIDE_ROUTE } from "@/components/page-info/pageInfoRegistry";

interface SidebarBottomMenusProps {
  isCollapsed: boolean;
}

/**
 * SidebarBottomMenus - Organism component
 *
 * Renders the menu entries pinned to the bottom of the navigation sidebar:
 * a plain "All Buildings" link (navigates to /buildings within the chosen
 * org, mirroring the top logo), then Organization and Profile — each of which
 * opens its own Radix DropdownMenu (rendered to the right of the sidebar).
 * The dropdown logic mirrors what CombinedMenuButton exposed from the (now
 * removed) header.
 *
 * "All Buildings" is a shortcut back to the My Buildings list, so it renders
 * on every surface this footer appears on (inside a building, settings, the
 * create form) — except the /buildings list itself, where you're already
 * there and it would be redundant.
 */
export const SidebarBottomMenus = observer(function SidebarBottomMenus({
  isCollapsed,
}: SidebarBottomMenusProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, language, setLanguage } = useLanguage();
  const { signOut } = useAuth();
  const { toast } = useToast();
  const { currentOrganization, userOrganizations, setCurrentOrganization, refetch } =
    useOrganizationContext();
  const permissions = usePermissions();
  const billing = useBillingStatus();
  const orgStore = useOrganizationStore();
  const authStore = useAuthStore();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [orgName, setOrgName] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [pendingOrgId, setPendingOrgId] = useState<string | null>(null);

  useEffect(() => {
    if (pendingOrgId && userOrganizations.length > 0) {
      const newOrg = userOrganizations.find((org) => org.id === pendingOrgId);
      if (newOrg) {
        setCurrentOrganization(newOrg);
        setPendingOrgId(null);
      }
    }
  }, [pendingOrgId, userOrganizations, setCurrentOrganization]);

  const handleLogout = async () => {
    try {
      await signOut();
      navigate("/login");
    } catch (error) {
      if (process.env.NODE_ENV === "development") {
        console.error("Error signing out:", error);
      }
    }
  };

  const handleOrgChange = (orgId: string) => {
    const selectedOrg = userOrganizations.find((org) => org.id === orgId);
    if (selectedOrg) {
      setCurrentOrganization(selectedOrg);
      navigate("/buildings");
    }
  };

  const handleCreateOrg = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!orgName.trim() || orgName.trim().length < 2) {
      toast({
        variant: "destructive",
        title: t?.common?.invalidName ?? "Invalid name",
        description:
          t?.common?.organizationNameMinLength ??
          "Organisation name must be at least 2 characters long.",
      });
      return;
    }

    const userId = authStore.userProfileId;
    if (!userId) {
      toast({
        variant: "destructive",
        title: t?.common?.error ?? "Error",
        description: t?.common?.userNotFound ?? "User not found. Please try logging in again.",
      });
      return;
    }

    setIsCreating(true);
    try {
      const result = await orgStore.createOrganization(orgName.trim(), userId);

      if (result.success && result.orgId) {
        await authStore.loadSession();
        setPendingOrgId(result.orgId);
        await refetch();

        setIsDialogOpen(false);
        setOrgName("");

        toast({
          title: t?.common?.success ?? "Success",
          description: (
            t?.common?.organizationCreatedSuccess ?? 'Organisation "{name}" created successfully.'
          ).replace("{name}", orgName.trim()),
        });
      } else {
        throw new Error(orgStore.error || "Failed to create organisation");
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: t?.common?.error ?? "Error",
        description: error instanceof Error ? error.message : "Failed to create organisation",
      });
    } finally {
      setIsCreating(false);
    }
  };

  // "All Buildings" is the shortcut back to the My Buildings list, so show it
  // on every page — except the /buildings list itself, where you're already
  // there and it would be redundant. (Trailing slashes are normalised so
  // `/buildings/` counts as the list too.)
  const isOnMyBuildingsList = location.pathname.replace(/\/+$/, "") === "/buildings";

  return (
    <>
      {!isOnMyBuildingsList && (
        <SidebarNavItem
          icon={LayoutGrid}
          label={t?.common?.allBuildings ?? "All Buildings"}
          href="/buildings"
          isCollapsed={isCollapsed}
        />
      )}

      <SidebarMenuItem
        icon={Building}
        label={currentOrganization?.name ?? t?.common?.organization ?? "Organisation"}
        isCollapsed={isCollapsed}
      >
        <DropdownMenuGroup>
          {userOrganizations.map((org) => (
            <DropdownMenuItem
              key={org.id}
              onClick={() => handleOrgChange(org.id)}
              className="gap-2"
            >
              <Check
                className={cn(
                  "h-4 w-4 shrink-0",
                  currentOrganization?.id === org.id ? "opacity-100" : "opacity-0"
                )}
              />
              <OrgLogo org={org} size={20} />
              {org.name}
            </DropdownMenuItem>
          ))}
          {/* No divider above it: it reads as the end of the organisation
              list, not as a section of its own. */}
          {permissions.isPlatformAdmin && (
            <DropdownMenuItem onClick={() => setIsDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              {t?.common?.createNewOrganization ?? "Create New Organisation"}
            </DropdownMenuItem>
          )}
        </DropdownMenuGroup>

        {(permissions.isAdmin || permissions.isPlatformAdmin) && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              {/* Billing shows negotiated rates, company legal data and the
                  invoice archive, so the entry is gated on the ORGANISATION-WIDE
                  role the database itself gates them on
                  (private.billing_org_manager = org_owner / org_admin) — not on
                  MANAGE_ORG_PERMISSIONS, which is a grant a role could hold
                  without being either, and which would offer a menu item whose
                  two pages answer 42501. New plan organisations need billing
                  setup even before their first paid building exists. */}
              {(permissions.isOrgOwner || permissions.isOrgAdmin) &&
                !billing.isLoading &&
                !hasLegacyBillingAccess(billing) &&
                (!billing.isPilot ||
                  billing.context?.allows_legacy_building_creation === false) && (
                  <DropdownMenuItem onClick={() => navigate("/billing")}>
                    {t.billing.nav.group}
                  </DropdownMenuItem>
                )}
              {permissions.isAdmin && (
                <DropdownMenuItem onClick={() => navigate("/organization-settings")}>
                  {t?.common?.organizationSettings ?? "Manage organisation"}
                </DropdownMenuItem>
              )}
              {permissions.isPlatformAdmin && (
                <DropdownMenuItem onClick={() => navigate("/admin/data-management")}>
                  <Shield className="mr-2 h-4 w-4" />
                  {t?.common?.adminDashboard ?? "Admin Panel"}
                </DropdownMenuItem>
              )}
            </DropdownMenuGroup>
          </>
        )}
      </SidebarMenuItem>

      <SidebarMenuItem
        icon={User}
        label={t?.common?.profile ?? "Profile"}
        isCollapsed={isCollapsed}
      >
        <DropdownMenuGroup>
          <DropdownMenuItem onClick={() => setLanguage("en")}>
            <Check
              className={cn("mr-2 h-4 w-4", language === "en" ? "opacity-100" : "opacity-0")}
            />
            {t.settings.languageEnglish}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setLanguage("et")}>
            <Check
              className={cn("mr-2 h-4 w-4", language === "et" ? "opacity-100" : "opacity-0")}
            />
            {t.settings.languageEstonian}
          </DropdownMenuItem>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate("/settings")}>
          {t?.common?.userSettings ?? "My settings"}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate(PAGE_INFO_GUIDE_ROUTE)}>
          {t.pageInfo.guide.title}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleLogout}>{t?.common?.logout ?? "Logout"}</DropdownMenuItem>
      </SidebarMenuItem>

      <Dialog
        open={isDialogOpen}
        onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) {
            setOrgName("");
            setPendingOrgId(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t?.common?.createOrgDialogTitle ?? "Create New Organisation"}
            </DialogTitle>
            <DialogDescription>
              {t?.common?.createOrgDialogDescription ??
                "Enter a name for your new organization. This name can be changed later."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateOrg}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="org-name">
                  {t?.common?.organizationNameLabel ?? "Organisation Name"}
                </Label>
                <Input
                  id="org-name"
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  placeholder={t?.common?.organizationNamePlaceholder ?? "Enter organisation name"}
                  disabled={isCreating}
                  autoFocus
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsDialogOpen(false);
                  setOrgName("");
                  setPendingOrgId(null);
                }}
                disabled={isCreating}
              >
                {t?.common?.cancel ?? "Cancel"}
              </Button>
              <Button type="submit" disabled={isCreating || !orgName.trim()}>
                {isCreating
                  ? (t?.common?.creating ?? "Creating...")
                  : (t?.common?.create ?? "Create")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
});
